import memoizeOne from "memoize-one";
import merge from "lodash/merge";
import { createTheme, type Theme, ThemeProvider, useTheme } from "@mui/material";
import { Form, type FormProps } from "react-admin";
import { FieldValues } from "react-hook-form";

const createFormThemeMemoized = memoizeOne((theme: Theme) => {
  const formTheme = createTheme(
    merge({}, theme, {
      components: {
        MuiInputLabel: {
          defaultProps: {
            shrink: true, // Always keep the label in the "shrink" position
          },
          styleOverrides: {
            root: {
              maxWidth: "fit-content",
              transform: "none",
              position: "relative",
              marginBottom: 5,
              fontSize: "12px",
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-notchedOutline": {
                top: 0,
                legend: {
                  display: "none", // Hide the legend to remove the gap
                },
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              "&.RaCheckboxGroupInput-label": {
                transform: "unset",
              },
            },
          },
        },
        MuiFormControl: {
          styleOverrides: {
            root: {
              marginTop: 0,
              marginBottom: 0,
              "& .RaSimpleFormIterator-action": {
                paddingTop: "20px",
              },
              "& .RaSimpleFormIterator-form .MuiFormLabel-root": {
                overflow: "unset",
                marginTop: "6px",
              },
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
              },
              marginLeft: "-4px",
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              padding: "4px",
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              padding: "4px",
            },
          },
        },
      },
    }) as any,
  );
  return formTheme;
});

export function Form2Theme({ children }: { children: React.ReactNode }) {
  const currentTheme = useTheme();
  const formTheme = createFormThemeMemoized(currentTheme);

  return <ThemeProvider theme={formTheme}>{children}</ThemeProvider>;
}

/**
 * Replacement for the `Form` component of react-admin which uses the new design:
 * - Input field labels are always rendered above an input field, even if the input field is empty.
 */
export function Form2<T = FieldValues>({
  children,
  ...formProps
}: Omit<FormProps, "className" | "onSubmit"> & { onSubmit?: (formData: T) => void | Promise<void> }) {
  return (
    <Form2Theme>
      <Form {...(formProps as any)} className="formTheme">
        {children}
      </Form>
    </Form2Theme>
  );
}
